import { cva } from 'class-variance-authority';

import type {
  StatusIndicatorMarker,
  StatusIndicatorMarkerColor,
  StatusIndicatorStatus,
} from './status-indicator.types';

/**
 * Semantic status → surface background. Each entry is the prescribed
 * `Chip`-aligned `bg-entity-{color}-muted` (or `bg-entity-neutral-muted` for
 * the neutral-surfaced statuses, `empty` and `archive`). Backgrounds flow
 * through the semantic layer and flip correctly between `newBrand` and
 * `newBrandDark`.
 *
 * The inner glyph colour is **not** owned by this component — each `Status*`
 * SVG hardcodes `fill="hsl(var(--twc-status-icon-<status>))"` so the glyph
 * paints the prescribed Figma hue regardless of wrapper or theme. See
 * `packages/design-tokens/src/semantic-colors.ts` (`statusIconColors`) for
 * the locked values.
 *
 * Note: unlike `Chip`'s `empty` variant (which uses a dashed border to signal
 * "no value yet"), `StatusIndicator`'s `empty` status has no border — the
 * dashed-circle `StatusEmpty` glyph carries that affordance instead.
 */
const semanticStatusClasses = {
  empty: 'bg-entity-neutral-muted',
  'to-do': 'bg-entity-purple-muted',
  'in-progress': 'bg-entity-blue-muted',
  'in-review': 'bg-entity-orange-muted',
  ready: 'bg-entity-green-muted',
  done: 'bg-entity-yellow-muted',
  alert: 'bg-entity-red-muted',
  archive: 'bg-entity-neutral-muted',
} as const satisfies Record<StatusIndicatorStatus, string>;

/**
 * Per-colour `bg` + `text` pair for the `marker` variant.
 *
 * Background: `bg-entity-{color}-muted` — the same scale step the semantic
 * statuses use, so `marker="dot" markerColor="blue"` and `status="in-progress"`
 * sit on identical surfaces.
 *
 * Foreground: `text-marker-icon-{color}` — a theme-locked token family that
 * mirrors `statusIconColors`' shade steps (see `markerIconColors` in
 * `packages/design-tokens/src/semantic-colors.ts`). The marker glyph paints
 * with `fill="currentColor"`, so wrapper text colour drives the fill — and
 * because both sides come from the marker-icon scale, every shape × colour
 * pairs at the same saturation as its semantic-status counterpart.
 */
const markerColorClasses = {
  neutral: 'bg-entity-neutral-muted text-marker-icon-neutral',
  red: 'bg-entity-red-muted text-marker-icon-red',
  orange: 'bg-entity-orange-muted text-marker-icon-orange',
  yellow: 'bg-entity-yellow-muted text-marker-icon-yellow',
  green: 'bg-entity-green-muted text-marker-icon-green',
  blue: 'bg-entity-blue-muted text-marker-icon-blue',
  purple: 'bg-entity-purple-muted text-marker-icon-purple',
  pink: 'bg-entity-pink-muted text-marker-icon-pink',
} as const satisfies Record<StatusIndicatorMarkerColor, string>;

/**
 * Marker shape lookup is exported purely so the component can branch on the
 * presence of `marker` without re-checking the union — see
 * `status-indicator.tsx`. Each entry is intentionally empty; marker
 * background+text come from `markerColorClasses` instead.
 */
const markerClasses = {
  dot: '',
  clover: '',
  star: '',
  incircle: '',
} as const satisfies Record<StatusIndicatorMarker, string>;

/** Frame (wrapper) size. Inner-glyph size lives in `glyphSizeClasses`. */
const sizeClasses = {
  xs: 'size-4', // 16px frame
  sm: 'size-6', // 24px frame
  md: 'size-8', // 32px frame
} as const;

/**
 * Stamped on the inner SVG. The `!` prefix hardens the prescribed size
 * against ancestor SVG-sizing rules (e.g. `CommandItem`'s unguarded
 * `[&_svg]:size-4`). Safe here because the glyph is internal — callers
 * only size via the `size` prop.
 */
const glyphSizeClasses = {
  xs: '!size-3', // 12px glyph
  sm: '!size-4', // 16px glyph
  md: '!size-6', // 24px glyph
} as const;

const statusIndicatorFrame = cva(
  'inline-flex shrink-0 items-center justify-center rounded-full [&>svg]:shrink-0',
  {
    variants: {
      size: sizeClasses,
    },
    defaultVariants: {
      size: 'sm',
    },
  }
);

export {
  glyphSizeClasses,
  markerClasses,
  markerColorClasses,
  semanticStatusClasses,
  statusIndicatorFrame,
};
